import { SideResponse } from "../codegen-api";
import { alignPriceToStep } from "./format.rounding";

export const getRange = (start: number, stop: number, step: number = 1) => {
  const a = [start];
  let b = start;
  while (b < stop) {
    a.push((b += step));
  }
  return a;
};

/**
 * Parse an optional API numeric string. Returns the finite number when present,
 * or `undefined` when the field is missing / empty / non-numeric — so callers can
 * distinguish a legitimate `"0"` from an absent field and fall back accordingly.
 * Use `apiNum(field) ?? clientComputation` to prefer API values with a fallback.
 */
export const apiNum = (v?: string | number | null): number | undefined => {
  if (v == null || v === "") {
    return undefined;
  }
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

// initialMargin in percentage. 100 = 100%, 1 = 1%
export const initialMarginUtilization = (
  equity: number,
  initialMargin: number,
  maintenanceMargin: number
) => {
  if (!equity) {
    return 0;
  }
  const utilization = ((initialMargin + maintenanceMargin) / equity) * 100;
  return utilization;
};

// maintenanceMargin in percentage. 100 = 100%, 1 = 1%
export const maintenanceMarginUtilization = (
  equity: number,
  maintenanceMargin: number
) => {
  if (!equity) {
    return 0;
  }
  const utilization = (maintenanceMargin / equity) * 100;
  return utilization;
};

// Returns the change from num1 -> num2
export const calculateChange = (num1: number, num2: number) => {
  let diff = 0;
  let percentageChange = 0;

  if (num1 && num2) {
    diff = num2 - num1;
    percentageChange = diff / num1;
  }

  return {
    diff,
    percentageChange,
  };
};

export const formatCompactCurrency = (number: number) => {
  const opt: Intl.NumberFormatOptions = {
    maximumFractionDigits: 2,
    notation: "compact",
    compactDisplay: "short",
    currencyDisplay: "narrowSymbol",
    style: "currency",
    currency: "USD",
  };
  return Intl.NumberFormat("en-US", opt).format(number);
};

export const calculatePnl = (
  totalEntryValue: number,
  totalExitValue: number,
  side: SideResponse
) =>
  side === SideResponse.Buy
    ? totalExitValue - totalEntryValue
    : totalEntryValue - totalExitValue;

export const calculatePnlPercent = (
  pnl: number,
  totalEntryValue: number,
  maintenanceMargin: number
) => (maintenanceMargin ? pnl / maintenanceMargin : pnl / totalEntryValue);

export const calculateExitPriceFromRoi = (
  totalEntryValue: number,
  roi: number, // 50% = 50
  maintenanceMargin: number,
  side: SideResponse,
  amount: number
): number => {
  if (amount === 0) {
    return 0;
  }
  const pnl = maintenanceMargin
    ? roi * maintenanceMargin
    : roi * totalEntryValue;

  if (side === SideResponse.Buy) {
    return Number((pnl / amount + totalEntryValue).toFixed(6));
  }
  return Number((totalEntryValue - pnl / amount).toFixed(6));
};

export const getPercent = (num: number, total: number) => {
  const percent = num / total;
  return Math.max(0, Math.min(percent, 1));
};

export const countDecimalPlaces = (number: number) => {
  const match = `${number}`.match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
  if (!match) return 0;

  // Extract the decimal part and the exponent part
  const [, decimalPart, exponentPart] = match;

  // Calculate the total decimal places by adding the decimal part length
  // and subtracting the exponent (if it exists and is negative)
  return Math.max(
    0,
    (decimalPart || "").length - (exponentPart ? +exponentPart : 0)
  );
};

export const nextSmallestDivisibleDecimal = (
  number: number,
  divisor: number
) => {
  if (divisor === 0) {
    return number;
  }

  let quotient = number / divisor;

  // if result ends with 0.999, ceil() instead. Caused by js accuracy errors
  // else, floor()
  quotient = String(quotient).split(".")[1]?.startsWith("999")
    ? Math.ceil(quotient)
    : Math.floor(quotient);

  const nextSmallestDivisible = quotient * divisor;

  // Return the rounded result to the desired decimal places
  const decimalPlaces = Math.max(
    countDecimalPlaces(number),
    countDecimalPlaces(divisor)
  );
  const roundedResult = Number(nextSmallestDivisible.toFixed(decimalPlaces));
  return roundedResult;
};

/**
 * Returns the number rounded to the nearest interval.
 * Example:
 *
 *   roundToNearest(1000.5, 1); // 1000
 *   roundToNearest(1000.5, 0.5);  // 1000.5
 *
 * Exact decimal arithmetic (shared with `alignPriceToStep`). Dividing in floats
 * mis-bucketed exact levels (2475.2 / 0.1 = 24751.999999999996 floored into
 * 2475.1), returned noisy values ("2475.1000000000004"), and a float epsilon
 * still dropped large levels on fine ticks a whole step.
 */
export const roundToNearest = (
  value: number,
  interval: number,
  roundUp?: boolean
): number => {
  if (!Number.isFinite(value) || !(interval > 0)) {
    return value;
  }
  return parseFloat(alignPriceToStep(value, interval, 0, roundUp ? "up" : "down"));
};

export const xScalarMultiplier = 500000;

export function calculateFarmBoostMultiplier(volume: number): number {
  // Constants
  const t = 0.936;
  const x0 = 3.2;
  const D = 1.2;
  const z = 0.092123;
  const p = 3.0796;
  const x = volume / xScalarMultiplier;

  if (x <= 0) {
    return 1;
  }
  // Check the range of x and apply the corresponding formula
  if (x > 0 && x < 4.562) {
    // Calculate the value based on the first equation
    const exponent = -D * (x - x0);
    const formulaValue = t + (4 - t) / (1 + Math.exp(exponent));
    return formulaValue;
  }
  if (x >= 4.562 && x < 10) {
    // Apply the second equation directly
    return Math.min(4, z * x + p);
  }

  // x is outside the defined ranges
  return 4;
}

export function getRoundedBoost(farmBoost: number) {
  let roundedFarmBoost = 10;

  if (farmBoost >= 100) {
    roundedFarmBoost = 100;
  } else if (farmBoost >= 50) {
    roundedFarmBoost = 50;
  }
  return roundedFarmBoost;
}
